import { ForwardRefExoticComponent, RefAttributes, forwardRef, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import { Avatar, Box, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { activeItem } from 'store/reducers/menu';
import IconsMenu from 'components/@extended/AntIconsMenu';
import { LinkTarget, NavItemType } from 'types/menu';
import { RootStateProps } from 'types/root';
import { navPathMatchesItem } from 'utils/navPathMatch';
import { getMenuIntlId } from 'utils/menuTranslation';

interface Props {
  item: NavItemType;
  level: number;
}

function isSafeNavUrl(url?: string): url is string {
  if (!url) return false;
  return !/(^|\/)(undefined|null)(\/|$|[?#])/i.test(url);
}

const NavItem = ({ item, level }: Props) => {
  const theme = useTheme();
  const intl = useIntl();
  const dispatch = useDispatch();
  const menu = useSelector((state: RootStateProps) => state.menu);
  const { drawerOpen } = menu;
  const router = useRouter();

  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps: {
    component: ForwardRefExoticComponent<RefAttributes<HTMLAnchorElement>> | string;
    href?: string;
    target?: LinkTarget;
  } = { component: 'div' };

  if (isSafeNavUrl(item.url)) {
    listItemProps = {
      component: forwardRef((props, ref) => <NextLink {...props} href={item.url as string} prefetch={false} />),
      target: itemTarget
    };
  }
  if (item?.external && isSafeNavUrl(item.url)) {
    listItemProps = { component: 'a', href: item.url ?? '#', target: itemTarget };
  }

  const Icon = item.icon!;
  const itemIcon = item.icon ? <IconsMenu name={Icon} prop={{ fontSize: '1rem' }} /> : false;

  const pathname = useMemo(
    () => (router.isReady ? router.asPath.split('?')[0] : ''),
    [router.isReady, router.asPath]
  );

  const isSelected = useMemo(
    () => navPathMatchesItem(pathname, isSafeNavUrl(item.url) ? item.url : undefined),
    [pathname, item.url]
  );

  useEffect(() => {
    if (!router.isReady || !isSafeNavUrl(item.url)) return;
    if (!navPathMatchesItem(pathname, item.url)) return;
    if (item.id) {
      dispatch(activeItem({ openItem: [item.id] }));
    }
  }, [router.isReady, pathname, item.id, item.url, dispatch]);

  const textColor = theme.palette.mode === 'dark' ? 'grey.400' : 'text.primary';
  const iconSelectedColor = theme.palette.mode === 'dark' && drawerOpen ? 'text.primary' : 'primary.main';
  const primary = theme.palette.primary.main;

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        textDecoration: 'none !important',
        borderRadius: drawerOpen ? 1.5 : 0,
        mx: drawerOpen ? 1.25 : 0,
        mb: 0.25,
        transition: 'all 0.2s ease',
        '& .MuiTypography-root': { textDecoration: 'none !important' },
        '&:hover .MuiTypography-root': { textDecoration: 'none !important' },
        pl: drawerOpen ? `${level * 28}px` : 1.5,
        py: !drawerOpen && level === 1 ? 1.25 : 0.875,
        ...(drawerOpen && {
          '&:hover': {
            bgcolor: alpha(primary, 0.06)
          },
          '&.Mui-selected': {
            bgcolor: alpha(primary, 0.08),
            borderRight: 'none',
            color: iconSelectedColor,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '25%',
              height: '50%',
              width: 3,
              borderRadius: '0 3px 3px 0',
              bgcolor: primary
            },
            '&:hover': {
              color: iconSelectedColor,
              bgcolor: alpha(primary, 0.1)
            }
          }
        }),
        ...(!drawerOpen && {
          '&:hover': { bgcolor: 'transparent' },
          '&.Mui-selected': {
            '&:hover': { bgcolor: 'transparent' },
            bgcolor: 'transparent'
          }
        })
      }}
    >
      {itemIcon && (
        <ListItemIcon sx={{ minWidth: 32, mr: 1.375, ml: -0.75 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1.5,
              color: isSelected ? primary : textColor,
              bgcolor: isSelected ? alpha(primary, 0.1) : alpha(theme.palette.text.secondary, 0.04),
              transition: 'all 0.2s ease',
              ...(!drawerOpen && { width: 36, height: 36, borderRadius: 1.5 })
            }}
          >
            {itemIcon}
          </Box>
        </ListItemIcon>
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <ListItemText
          sx={{ ml: -0.5 }}
          primary={
            <Typography
              variant="body1"
              sx={{
                color: isSelected ? iconSelectedColor : textColor,
                fontWeight: isSelected ? 600 : 400,
                fontSize: '0.875rem',
                transition: 'color 0.2s ease, font-weight 0.2s ease'
              }}
            >
              {(() => { const intlId = getMenuIntlId(item); return intlId ? intl.formatMessage({ id: intlId, defaultMessage: String(item.title ?? '') }) : item.title; })()}
            </Typography>
          }
        />
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
        <Chip
          {...item.chip}
          color={item.chip.color ?? 'default'}
          avatar={item.chip.avatar ? <Avatar>{item.chip.avatar}</Avatar> : undefined}
        />
      )}
    </ListItemButton>
  );
};

export default NavItem;
